import React, { ReactElement } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { filterProvider } from "helpers/addons";
import { formatDateColor } from "helpers/app";

import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { locationList } from "constants/locations";

import CloudbucketActions from "components/addons/cloudbucket/CloudbucketActions";
import AdminLogAsUser from "components/shared/admin/AdminLogAsUser";
import IconSelector from "components/shared/images/IconSelector";
import GenericStatus from "components/shared/status/GenericStatus";

import { IAllServers } from "types/api/serversApiInterface";
import { cloudbucketTableData } from "types/global/table";

const CloudbucketTable = ({
 rowData,
 index,
 servers
}: {
 rowData: cloudbucketTableData;
 index: number;
 servers: Array<IAllServers>;
}): ReactElement => {
 const RenderLocation = (tableLocation: string) => {
  const location = formatLocation(tableLocation);
  const filteredLocation = locationList.filter((data) => location === data.tag);

  return filteredLocation.map((element, index) => (
   <Stack key={`location-item-${index}`} direction="row" spacing={1}>
    <Stack component="span" className={`fi fi-${element.flag}`} />
    <Typography variant="inherit">
     <FormattedMessage id={element.location} />
    </Typography>
   </Stack>
  ));
 };

 const formatLocation = (location: string): string => {
  switch (location) {
   case "european union":
    return "eu";
   default:
    return location;
  }
 };

 const RenderStatus = ({ status }: { status: string }) => {
  switch (status) {
   case "active":
    return (
     <IconSelector icon="StatusIcon" props={{ style: { color: "green" }, fontSize: "small" }} />
    );
   case "expiring":
    return (
     <IconSelector icon="ReportIcon" props={{ style: { color: "yellow" }, fontSize: "small" }} />
    );
   case "expired":
    return (
     <IconSelector
      icon="ReportProblemIcon"
      props={{ style: { color: "#FF1800" }, fontSize: "small" }}
     />
    );
   default:
    return (
     <IconSelector icon="StatusIcon" props={{ style: { color: "green" }, fontSize: "small" }} />
    );
  }
 };

 const RenderSystemStatus = ({ status }: { status: string }) => {
  switch (status) {
   case "online":
    return <GenericStatus status="online" />;
   case "creating":
    return <GenericStatus status="creating" />;
   case "updating":
    return <GenericStatus status="updating" />;
   case "expired":
    return <GenericStatus status="expired" />;
   case null:
    return <GenericStatus status={null} />;
   default:
    return <GenericStatus status="offline" />;
  }
 };

 return (
  <TableRow
   sx={{ cursor: rowData.status === "expired" ? "not-allowed" : "pointer" }}
   key={`volumes-list-${index}`}>
   <TableCell component="th" scope="row">
    <Stack direction="column">
     <Stack direction="row" alignItems="center" spacing={1}>
      <img
       key={`provider-key-${index}`}
       width={20}
       height={20}
       src={filterProvider(rowData.provider)}
       alt="provider"
      />
      <RenderStatus status={rowData.status} />
      <Typography variant="inherit" fontWeight="bold">
       {rowData.name}
      </Typography>
     </Stack>
     {RenderLocation(rowData.region.toLowerCase())}
     <Stack onClick={(event) => event.stopPropagation()} maxWidth="fit-content">
      <AdminLogAsUser userid={rowData.userid || ""} owner={rowData.personal_name || ""} />
     </Stack>
    </Stack>
   </TableCell>
   <TableCell component="th" scope="row">
    <RenderSystemStatus status={rowData.bucketStatus} />
   </TableCell>
   <TableCell component="th" scope="row">
    {rowData.server_id_db ? (
     <Stack direction="row" spacing={1} alignItems="center">
      <IconSelector icon="DvrIcon" />
      <Typography variant="caption">
       {servers.find((element) => element.id === rowData.server_id_db)?.server_name}
      </Typography>
     </Stack>
    ) : (
     <Stack direction="row" spacing={1} alignItems="center">
      <IconSelector icon="DvrIcon" />
      <Typography variant="caption">No server connected</Typography>
     </Stack>
    )}
   </TableCell>
   <TableCell component="th" scope="row">
    {rowData.totalPurchasedSpaceTB ? (
     `${rowData.totalPurchasedSpaceTB} TB`
    ) : (
     <Typography variant="caption" fontStyle="italic">
      Not available
     </Typography>
    )}
   </TableCell>
   <TableCell>
    <Stack>
     <Typography variant="caption" color={formatDateColor(rowData.status)}>
      <FormattedDate value={rowData.expire} />
     </Typography>
     {rowData.status === "expired" && (
      <Typography variant="caption" color="error">
       {rowData.status}
      </Typography>
     )}
    </Stack>
   </TableCell>
   {rowData.bucketStatus === "creating" || rowData.bucketStatus === "updating" ? (
    <TableCell component="th" scope="row" align="right">
     <Typography variant="caption" fontStyle="italic">
      No action available
     </Typography>
    </TableCell>
   ) : (
    <TableCell component="th" scope="row" align="right">
     <CloudbucketActions rowData={rowData} servers={servers} />
    </TableCell>
   )}
  </TableRow>
 );
};

export default CloudbucketTable;
